// @ts-nocheck
'use client'

/**
 * Lightweight versions of heavy AI Element components.
 * These avoid Shiki (syntax highlighter), Streamdown (markdown renderer),
 * and Mermaid — the main causes of OOM during Next.js dev compilation.
 * 
 * They replicate the same UI/UX using simple primitives.
 */

import { useState, useRef, useEffect, useCallback, memo, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { copyToClipboard } from '@/lib/clipboard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  BrainIcon, ChevronDownIcon, WrenchIcon, CopyIcon, CheckIcon,
  CheckCircleIcon, ClockIcon, CircleIcon, XCircleIcon, Loader2Icon
} from 'lucide-react'

// ════════════════════════════════════════════════════════════════
// Reasoning (lightweight — plain text instead of Streamdown)
// ════════════════════════════════════════════════════════════════

export const LiteReasoning = memo(function LiteReasoning({
  className, isStreaming = false, defaultOpen = false, children
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen || isStreaming)
  const [duration, setDuration] = useState(undefined)
  const startRef = useRef(null)

  useEffect(() => {
    if (isStreaming) {
      if (!startRef.current) startRef.current = Date.now()
      if (!isOpen) setIsOpen(true)
    } else if (startRef.current) {
      setDuration(Math.ceil((Date.now() - startRef.current) / 1000))
      startRef.current = null
      const t = setTimeout(() => setIsOpen(false), 1000)
      return () => clearTimeout(t)
    }
  }, [isStreaming])

  const triggerText = isStreaming
    ? 'Thinking...'
    : duration !== undefined
      ? `Thought for ${duration} seconds`
      : 'Thought for a few seconds'

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={cn('not-prose mb-4', className)}>
      <CollapsibleTrigger className="flex w-full items-center gap-2 text-muted-foreground text-sm transition-colors hover:text-foreground">
        <BrainIcon className="size-4" />
        <span className={isStreaming ? 'animate-pulse' : ''}>{triggerText}</span>
        <ChevronDownIcon className={cn('size-4 transition-transform', isOpen ? 'rotate-180' : 'rotate-0')} />
      </CollapsibleTrigger>
      <CollapsibleContent className="mt-4 text-sm text-muted-foreground data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=open]:slide-in-from-top-2">
        <div className="whitespace-pre-wrap leading-relaxed">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  )
})

// ════════════════════════════════════════════════════════════════
// CodeBlock (lightweight — no Shiki, uses <pre><code> with CSS)
// ════════════════════════════════════════════════════════════════

export const LiteCodeBlock = memo(function LiteCodeBlock({
  code, language = 'text', filename, showLineNumbers = false, className
}) {
  const [copied, setCopied] = useState(false)
  const lines = useMemo(() => (code || '').split('\n'), [code])

  const handleCopy = useCallback(async () => {
    await copyToClipboard(code || '')
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [code])

  return (
    <div className={cn('group relative w-full overflow-hidden rounded-md border bg-background', className)}>
      <div className="flex items-center justify-between border-b bg-muted/80 px-3 py-2 text-muted-foreground text-xs">
        <span className="font-mono">{filename || language}</span>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={handleCopy}>
          {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
        </Button>
      </div>
      <div className="relative overflow-auto">
        <pre className="m-0 p-4 text-sm bg-card/50">
          <code className="font-mono text-sm">
            {lines.map((line, i) => (
              <span key={i} className="block">
                {showLineNumbers && (
                  <span className="inline-block w-8 mr-4 text-right text-muted-foreground/50 select-none">{i + 1}</span>
                )}
                {line || '\n'}
              </span>
            ))}
          </code>
        </pre>
      </div>
    </div>
  )
})

// ════════════════════════════════════════════════════════════════
// Tool (lightweight — uses LiteCodeBlock instead of Shiki-based)
// ════════════════════════════════════════════════════════════════

const statusLabels = {
  'approval-requested': 'Awaiting Approval',
  'approval-responded': 'Responded',
  'input-available': 'Running',
  'input-streaming': 'Pending',
  'output-available': 'Completed',
  'output-denied': 'Denied',
  'output-error': 'Error',
}

const statusIcons = {
  'approval-requested': <ClockIcon className="size-4 text-yellow-600" />,
  'approval-responded': <CheckCircleIcon className="size-4 text-blue-600" />,
  'input-available': <ClockIcon className="size-4 animate-pulse" />,
  'input-streaming': <Loader2Icon className="size-4 animate-spin" />,
  'output-available': <CheckCircleIcon className="size-4 text-green-600" />,
  'output-denied': <XCircleIcon className="size-4 text-orange-600" />,
  'output-error': <XCircleIcon className="size-4 text-red-600" />,
}

export const LiteTool = memo(function LiteTool({
  title, type, state = 'output-available', input, output, defaultOpen = false
}) {
  const derivedName = title || (type ? type.split('-').slice(1).join(' ') : 'Tool')

  return (
    <Collapsible className="group not-prose mb-4 w-full rounded-md border" defaultOpen={defaultOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-4 p-3">
        <div className="flex items-center gap-2">
          <WrenchIcon className="size-4 text-muted-foreground" />
          <span className="font-medium text-sm">{derivedName}</span>
          <Badge className="gap-1.5 rounded-full text-xs" variant="secondary">
            {statusIcons[state]}
            {statusLabels[state]}
          </Badge>
        </div>
        <ChevronDownIcon className="size-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-4 p-4 data-[state=open]:animate-in data-[state=open]:slide-in-from-top-2">
        {input && (
          <div className="space-y-2">
            <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">Parameters</h4>
            <LiteCodeBlock code={JSON.stringify(input, null, 2)} language="json" />
          </div>
        )}
        {output && (
          <div className="space-y-2">
            <h4 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">Result</h4>
            <LiteCodeBlock code={JSON.stringify(output, null, 2)} language="json" />
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  )
})


// ════════════════════════════════════════════════════════════════
// Message (lightweight — no Streamdown, uses ReactMarkdown)
// Mimics the ai-elements Message layout structure
// ════════════════════════════════════════════════════════════════

export const LiteMessage = memo(function LiteMessage({ from = 'assistant', className, children }) {
  return (
    <div className={cn(
      'flex gap-3',
      from === 'user' ? 'flex-row-reverse' : 'flex-row',
      className
    )} data-testid={`lite-message-${from}`}>
      {children}
    </div>
  )
})

export const LiteMessageContent = memo(function LiteMessageContent({ className, children }) {
  return (
    <div className={cn('flex-1 min-w-0 space-y-3', className)}>
      {children}
    </div>
  )
})

export const LiteMessageActions = memo(function LiteMessageActions({ className, children }) {
  return (
    <div className={cn('flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity', className)}>
      {children}
    </div>
  )
})
